/**
 * Waitlist / request early access — the primary conversion page (every homepage
 * "Request early access" CTA lands here). Court-exhibit auth shell (AuthLayout +
 * AuthBrandPanel) wrapping the hand-built waitlist form. Submission logic
 * (api.post /api/waitlist) + the success state are unchanged.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import AuthLayout from '../components/auth/AuthLayout';
import { AuthField, AuthSelect, AuthSubmit, AuthError } from '../components/auth/authForm';

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    try {
      await api.post('/api/waitlist', formData, { showErrorToast: false });
      setStatus('success');
    } catch (error: any) {
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (status === 'success') {
    return (
      <AuthLayout title="You’re on the list" subtitle="Thanks for your interest in The Figure Co." framed={false}>
        <p className="font-body text-sm leading-relaxed text-text-secondary">
          We’ll reach out when we’re ready to bring your firm on board — only about access, never a
          marketing list.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex font-mono text-xs uppercase tracking-[0.12em] text-text-primary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-brand-text hover:decoration-current"
          >
            Back to homepage
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Request early access"
      subtitle="Private beta — request access for your firm or claim team."
      altPrompt="Already have an account?"
      altLinkText="Sign in"
      altLinkTo="/sign-in"
      framed={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField id="firstName" name="firstName" label="First name" value={formData.firstName} onChange={handleChange} required placeholder="John" autoComplete="given-name" />
          <AuthField id="lastName" name="lastName" label="Last name" value={formData.lastName} onChange={handleChange} required placeholder="Doe" autoComplete="family-name" />
        </div>
        <AuthField id="email" name="email" label="Work email" type="email" value={formData.email} onChange={handleChange} required placeholder="john@firm.com" autoComplete="email" />
        <AuthField id="company" name="company" label="Firm / organization" value={formData.company} onChange={handleChange} placeholder="Your firm or organization" autoComplete="organization" />
        <AuthSelect id="role" name="role" label="Your role" value={formData.role} onChange={handleChange}>
          <option value="">Select your role…</option>
          <option value="attorney">Plaintiff attorney</option>
          <option value="accountant">CPA / Accountant</option>
          <option value="adjuster">Carrier adjuster</option>
          <option value="data_specialist">Data specialist</option>
          <option value="neutral">Neutral / Mediator</option>
          <option value="client">Client / Policyholder</option>
          <option value="other">Other</option>
        </AuthSelect>

        {status === 'error' && <AuthError>{errorMessage}</AuthError>}

        <AuthSubmit loading={status === 'loading'} loadingText="Submitting…">
          Request early access
        </AuthSubmit>

        <p className="font-mono text-[11px] leading-relaxed text-text-dim">
          We’ll only email you about access — no marketing list. By submitting you agree to our{' '}
          <Link to="/privacy" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-secondary hover:decoration-current">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-secondary hover:decoration-current">
            Terms of Service
          </Link>.
        </p>
      </form>
    </AuthLayout>
  );
}
